/**
 * @engine/vfx — GPU event data + particle-attribute layout helpers.
 */
import type { VFXDataType } from './data';

/**
 * GPU Event data - written when particles die, read when spawning.
 * Must match WGSL struct GPUEvent.
 */
export interface GPUEventData {
  position: { x: number; y: number; z: number };
  velocity: { x: number; y: number; z: number };
  color: { r: number; g: number; b: number; a: number };
  spawnCount: number;
  eventId: number;
}

// ============================================================================
// PARTICLE ATTRIBUTES
// ============================================================================

/**
 * Built-in particle attributes (available in shaders).
 */
export interface ParticleAttributes {
  // Required
  position: 'vec3';
  lifetime: 'float';     // Current age
  maxLifetime: 'float';  // Total lifetime

  // Optional built-in
  velocity?: 'vec3';
  color?: 'vec4';
  size?: 'float';
  rotation?: 'float';
  angularVelocity?: 'float';
  seed?: 'uint';         // Per-particle random seed

  // Custom attributes
  [key: string]: VFXDataType | undefined;
}

/**
 * Default particle attributes.
 */
export const DEFAULT_PARTICLE_ATTRIBUTES: Readonly<ParticleAttributes> = {
  position: 'vec3',
  lifetime: 'float',
  maxLifetime: 'float',
  velocity: 'vec3',
  color: 'vec4',
  size: 'float',
  scale: 'vec2',
  rotation: 'float',
  angularVelocity: 'float',
  spriteFrame: 'float',  // For flipbook animation
  seed: 'uint',
};

/**
 * Calculate particle stride in bytes.
 */
export function calculateParticleStride(attributes: ParticleAttributes): number {
  const typeSizes: Record<VFXDataType, number> = {
    bool: 4,
    int: 4,
    uint: 4,
    float: 4,
    vec2: 8,
    vec3: 12,
    vec4: 16,
    color: 16,
    mat3: 36,
    mat4: 64,
    texture2d: 0,
    texture3d: 0,
    pointmap: 0,
    gradient: 0,
    curve: 0,
    flow: 0,
    event: 0,
    spawnEvent: 0,
    particle: 0,
  };

  let stride = 0;
  for (const type of Object.values(attributes)) {
    if (type) {
      stride += typeSizes[type as VFXDataType] || 0;
    }
  }
  return stride;
}
